import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';
import { format, parseISO, getDay } from 'date-fns';

const DailyFrequencyChart = ({ activitiesData }) => {
    const containerRef = useRef(null);
    const [showInfo, setShowInfo] = useState(false);

    useEffect(() => {
        if (!containerRef.current || !activitiesData || activitiesData.length === 0) return;

        d3.select(containerRef.current).selectAll("svg").remove();

        const container = containerRef.current;
        const { width, height } = container.getBoundingClientRect();
        const margin = { top: 15, right: 20, bottom: 20, left: 45 };
        const chartWidth = width - margin.left - margin.right;
        const chartHeight = height - margin.top - margin.bottom;

        const svg = d3.select(container)
            .append("svg")
            .attr("viewBox", `0 0 ${width} ${height}`)
            .attr("preserveAspectRatio", "xMidYMid meet")
            .style("width", "100%")
            .style("height", "100%")
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        const dailyData = processDataByWeekdays(activitiesData);

        const x = d3.scaleBand()
            .domain(dailyData.map(d => d.day))
            .range([0, chartWidth])
            .padding(0.2);

        const maxActivities = d3.max(dailyData, d => d.count);
        const yDomainMax = maxActivities * 1.2;
        const y = d3.scaleLinear()
            .domain([0, yDomainMax])
            .nice()
            .range([chartHeight, 0]);

        svg.append("g")
            .attr("transform", `translate(0,${chartHeight})`)
            .call(d3.axisBottom(x))
            .selectAll("text")
            .style("text-anchor", "middle");

        svg.append("g")
            .call(d3.axisLeft(y).ticks(5).tickFormat(d3.format("d")));

        svg.append("text")
            .attr("text-anchor", "middle")
            .attr("transform", "rotate(-90)")
            .attr("y", -margin.left + 15)
            .attr("x", -chartHeight / 2)
            .text("Number");

        const getBarColor = (day) => {
            if (day === 'Sun' || day === 'Sat') {
                return '#ff9800';
            }
            return '#2196f3';
        };

        svg.selectAll(".bar")
            .data(dailyData)
            .enter()
            .append("rect")
            .attr("class", "bar")
            .attr("x", d => x(d.day))
            .attr("width", x.bandwidth())
            .attr("y", d => y(d.count))
            .attr("height", d => chartHeight - y(d.count))
            .attr("fill", d => getBarColor(d.day))
            .attr("stroke", d => d3.color(getBarColor(d.day)).darker(0.5))
            .attr("stroke-width", 1);

        svg.selectAll(".label")
            .data(dailyData)
            .enter()
            .append("text")
            .attr("class", "label")
            .attr("text-anchor", "middle")
            .attr("x", d => x(d.day) + x.bandwidth() / 2)
            .attr("y", d => y(d.count) - 5)
            .attr("font-size", "12px")
            .attr("fill", "#333")
            .text(d => d.count);

    }, [activitiesData]);

    const processDataByWeekdays = (activities) => {
        if (!activities || activities.length === 0) return [];

        const daysOfWeek = [
            { day: 'Mon', dayNum: 1, count: 0 },
            { day: 'Tue', dayNum: 2, count: 0 },
            { day: 'Wed', dayNum: 3, count: 0 },
            { day: 'Thu', dayNum: 4, count: 0 },
            { day: 'Fri', dayNum: 5, count: 0 },
            { day: 'Sat', dayNum: 6, count: 0 },
            { day: 'Sun', dayNum: 0, count: 0 }
        ];

        activities.forEach(activity => {
            if (!activity.date) return;
            
            try {
                const date = parseISO(activity.date);
                const dayNum = getDay(date);
                const dayIndex = daysOfWeek.findIndex(d => d.dayNum === dayNum);
                
                if (dayIndex !== -1) {
                    daysOfWeek[dayIndex].count++;
                }
            } catch (error) {
                console.error("Error parsing date:", error);
            }
        });

        return [
            daysOfWeek[0],
            daysOfWeek[1],
            daysOfWeek[2],
            daysOfWeek[3],
            daysOfWeek[4],
            daysOfWeek[5],
            daysOfWeek[6]
        ];
    };

    return (
        <ChartWrapper>
            <TitleContainer>
                <Title>
                    Daily Activities Submission
                    <InfoIconContainer 
                        onMouseEnter={() => setShowInfo(true)}
                        onMouseLeave={() => setShowInfo(false)}
                    >
                        <InfoIcon>ⓘ</InfoIcon>
                        {showInfo && (
                            <InfoTooltip>
                                <InfoTitle>Daily Activities Chart Information</InfoTitle>
                                <InfoItem>
                                    <strong>Bar Chart:</strong> Shows total activity submissions for each day of the week
                                </InfoItem>
                                <InfoItem>
                                    <strong>Color Coding:</strong> Weekdays (Mon-Fri) in blue, weekends (Sat-Sun) in orange
                                </InfoItem>
                                <InfoItem>
                                    <strong>Bar Heights:</strong> Represent the total number of activities submitted on each day
                                </InfoItem>
                                <InfoItem>
                                    <strong>Data Labels:</strong> Numbers above bars show exact count for each day
                                </InfoItem>
                            </InfoTooltip>
                        )}
                    </InfoIconContainer>
                </Title>
            </TitleContainer>
            <ChartContainer ref={containerRef} />
        </ChartWrapper>
    );
};

const ChartContainer = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    height: 100%;
    background-color: #f0f0f0;
    position: relative;
    overflow: hidden;
    display: flex;
    padding: 3px 5px;
    justify-content: center;
    align-items: center;
    
    & svg {
        max-width: 100%;
        max-height: 100%;
    }
`;

const ChartWrapper = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: visible;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0px;
    position: relative;
    flex-shrink: 0;
    height: 25px;
    overflow: visible;
`;

const Title = styled.h3`
    font-size: 14px;
    margin: 0;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #333;
`;

const InfoIconContainer = styled.div`
    position: relative;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const InfoIcon = styled.div`
    font-size: 12px;
    color: #666;
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #e0e0e0;
        border-color: #999;
        color: #333;
    }
`;

const InfoTooltip = styled.div`
    position: absolute;
    bottom: 25px;
    left: -280px;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    width: 300px;
    z-index: 9999;
`;

const InfoTitle = styled.h4`
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 10px 0;
    color: #333;
    text-align: center;
    border-bottom: 1px solid #eee;
    padding-bottom: 6px;
`;

const InfoItem = styled.div`
    font-size: 12px;
    margin: 6px 0;
    line-height: 1.4;
    color: #555;
`;

export default React.memo(DailyFrequencyChart); 